// Dev-only mock data. Enabled when VITE_DEV_MOCK=1 so the desktop UI can be
// explored without a running backend/Postgres. Everything here is canned data.

export const MOCK_ENABLED = import.meta.env.VITE_DEV_MOCK === '1';

const MOCK_PROJECTS = [
  {
    id: 'mock-project-1',
    name: 'MyWebApp',
    googleDriveId: 'mock-folder-webapp',
    folderPath: '/workspace/mock-project-1',
    createdAt: new Date().toISOString(),
    updatedAt: new Date(Date.now() - 3600_000).toISOString(),
  },
  {
    id: 'mock-project-2',
    name: 'marketing-site',
    googleDriveId: 'mock-folder-marketing',
    folderPath: '/workspace/mock-project-2',
    createdAt: new Date(Date.now() - 86400_000 * 2).toISOString(),
    updatedAt: new Date(Date.now() - 600_000).toISOString(),
  },
];

const MOCK_DRIVE_ROOT = [
  { id: 'mock-folder-webapp', name: 'MyWebApp', mimeType: 'application/vnd.google-apps.folder', size: '0', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-folder-marketing', name: 'marketing-site', mimeType: 'application/vnd.google-apps.folder', size: '0', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-file-readme', name: 'README.md', mimeType: 'text/markdown', size: '1240', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
];

const MOCK_DRIVE_WEBAPP = [
  { id: 'mock-folder-src', name: 'src', mimeType: 'application/vnd.google-apps.folder', size: '0', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-folder-node_modules', name: 'node_modules', mimeType: 'application/vnd.google-apps.folder', size: '0', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-file-pkg', name: 'package.json', mimeType: 'application/json', size: '512', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
];

const MOCK_DRIVE_SRC = [
  { id: 'mock-folder-components', name: 'components', mimeType: 'application/vnd.google-apps.folder', size: '0', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-file-app', name: 'App.tsx', mimeType: 'text/typescript', size: '1534', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
  { id: 'mock-file-index', name: 'index.css', mimeType: 'text/css', size: '821', createdTime: new Date().toISOString(), modifiedTime: new Date().toISOString() },
];

const MOCK_PROJECT_TREE = {
  tree: [
    { name: 'src', path: 'src', type: 'directory', children: [
      { name: 'App.tsx', path: 'src/App.tsx', type: 'file' },
      { name: 'main.tsx', path: 'src/main.tsx', type: 'file' },
      { name: 'components', path: 'src/components', type: 'directory', children: [
        { name: 'Header.tsx', path: 'src/components/Header.tsx', type: 'file' },
      ]},
    ]},
    { name: 'package.json', path: 'package.json', type: 'file' },
    { name: 'index.html', path: 'index.html', type: 'file' },
    { name: 'vite.config.ts', path: 'vite.config.ts', type: 'file' },
  ],
};

export const MOCK_FILES: Record<string, string> = {
  'package.json': JSON.stringify({
    name: 'my-webapp',
    private: true,
    version: '1.0.0',
    type: 'module',
    scripts: { dev: 'vite', build: 'tsc && vite build', preview: 'vite preview' },
    dependencies: { react: '^18.2.0', 'react-dom': '^18.2.0' },
  }, null, 2),
  'src/App.tsx': `import { useState } from 'react'\n\n// Mock file - edit me from the terminal or the editor.\nexport function App() {\n  const [count, setCount] = useState(0)\n  return (\n    <div style={{ fontFamily: 'sans-serif', padding: 24 }}>\n      <h1>Hello from Cloud Desktop 🚀</h1>\n      <button onClick={() => setCount(c => c + 1)}>count: {count}</button>\n    </div>\n  )\n}\n`,
  'src/main.tsx': `import { createRoot } from 'react-dom/client'\nimport { App } from './App'\n\ncreateRoot(document.getElementById('root')!).render(<App />)\n`,
  'src/components/Header.tsx': `export function Header() {\n  return <header>Cloud Desktop</header>\n}\n`,
  'index.html': `<!doctype html>\n<html>\n  <head><title>MyWebApp</title></head>\n  <body>\n    <div id="root"></div>\n    <script type="module" src="/src/main.tsx"></script>\n  </body>\n</html>\n`,
  'vite.config.ts': `import { defineConfig } from 'vite'\nimport react from '@vitejs/plugin-react'\n\nexport default defineConfig({ plugins: [react()] })\n`,
};

export function mockRequest(
  path: string,
  method: string,
  _body: any,
): any {
  const m = (p: string) => path.startsWith(p);

  // Auth
  if (m('/api/auth/check')) {
    return { authenticated: true, hasGoogleAccess: true };
  }
  if (m('/api/auth/login')) return { success: true };
  if (m('/api/auth/logout')) return { success: true };
  if (m('/api/auth/google')) return { url: '/api/auth/google' };

  // Drive
  if (m('/api/drive/search')) {
    const q = (path.split('query=')[1] || '').replace(/\+/g, ' ');
    return { files: MOCK_DRIVE_SRC.filter((f) => f.name.toLowerCase().includes(q.toLowerCase())) };
  }
  if (m('/api/drive/files')) {
    if (method === 'POST') {
      return { file: { id: `mock-${Date.now()}`, name: _body?.name || 'new-file.txt', mimeType: _body?.mimeType || 'text/plain' } };
    }
    return { files: MOCK_DRIVE_ROOT };
  }
  if (method === 'GET' && m('/api/drive/files/mock-folder-webapp')) {
    return { file: { id: 'mock-folder-webapp', name: 'MyWebApp', mimeType: 'application/vnd.google-apps.folder' } };
  }
  if (method === 'GET' && m('/api/drive/files/')) {
    const id = path.split('/api/drive/files/')[1]?.split('?')[0];
    return { file: { id, name: id || 'file', mimeType: 'text/plain' } };
  }
  if (m('/api/drive/folders') && method === 'POST') {
    return { folder: { id: `mock-folder-${Date.now()}`, name: _body?.name || 'folder' } };
  }
  if (m('/api/drive/files/') && (method === 'DELETE' || method === 'PUT')) {
    return { success: true };
  }
  if (m('/api/drive/files/') && m('/move') && method === 'POST') return { file: {} };
  if (m('/api/drive/files/') && m('/copy') && method === 'POST') return { file: { id: `mock-copy-${Date.now()}` } };

  // Projects
  if (m('/api/projects') && method === 'GET') return { projects: MOCK_PROJECTS };
  if (m('/api/projects') && method === 'POST') {
    const project = {
      id: `mock-project-${Date.now()}`,
      name: _body?.name || 'new-project',
      googleDriveId: `mock-folder-${Date.now()}`,
      folderPath: `/workspace/${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    MOCK_PROJECTS.unshift(project);
    return { project };
  }
  if (m('/api/projects/mock-project-1/files')) return MOCK_PROJECT_TREE;
  if (m('/api/projects/mock-project-2/files')) {
    return { tree: [
      { name: 'README.md', path: 'README.md', type: 'file' },
      { name: 'index.html', path: 'index.html', type: 'file' },
    ]};
  }
  if (m('/api/projects/mock-project-') && m('/file')) {
    const p = path.split('path=')[1] || '';
    const decoded = decodeURIComponent(p).replace(/\+/g, ' ');
    if (method === 'GET') return { path: decoded, content: MOCK_FILES[decoded] ?? `// mock content for ${decoded}\n`, size: 100, modifiedTime: new Date().toISOString() };
    if (method === 'PUT') return { success: true };
    if (method === 'DELETE') return { success: true };
  }
  if (m('/api/projects/mock-project-') && m('/folder') && method === 'POST') return { success: true };
  if (m('/api/projects/mock-project-') && ('/sync') && method === 'POST') return { synced: 0, errors: [] };
  if (m('/api/projects/') && method === 'DELETE') return { success: true };

  // Preview
  if (m('/api/preview/status')) return { running: false };
  if (m('/api/preview/start')) return { port: 5173, url: 'http://localhost:5173', projectId: 'mock' };
  if (m('/api/preview/stop')) return { success: true, stopped: false };

  // Settings
  if (m('/api/settings')) return { settings: { theme: 'dark', fontSize: '14' } };

  // Chat
  if (m('/api/chat/status')) return { configured: false, model: 'gpt-4o-mini', streamSupported: true };

  return { ok: true };
}

export function mockDownload(fileId: string): Blob {
  const content = MOCK_FILES[fileId] || `mock download content for ${fileId}`;
  return new Blob([content], { type: 'text/plain' });
}